
   import { defineComponent } from "vue";
   import { getAuthToken } from "@/security";
   import { Trip, Signup } from "@/ApiInterfaces";
   import CmsHeader from "@/components/CmsHeader.vue";
   import CmsBreadcrumbs from "@/components/CmsBreadcrumbs.vue";
   import StateSelect from "@/components/StateSelect.vue";
   import ModalLoading from "@/components/ModalLoading.vue";
   import ModalErrorMessage from "@/components/ModalErrorMessage.vue";
   import ModalInfoMessage from "@/components/ModalInfoMessage.vue";
   import { groupBy, formatPhoneNumber, generateGuid } from "@/main";
   import { parseApiError, fieldError, getFieldErrorMsg, hasFieldError } from "@/apiErrorHandling";

   interface RLData {
      trip: null | Trip;
      signups: Signup[];
      draggingPax: null | Signup;
      draggingOverElement: null | HTMLElement;
      draggingOverCount: number;
      loading: boolean;
      errorMessage: null | string;
      infoMessage: null | string;
      fieldErrors: null | fieldError[];
      showingPaxDetailsId: null | string;
      paxDetailsTimer: null | number;
      unsavedChanges: boolean;
      dataLoaded: boolean;
      animateSaved: boolean;
      animateMoved: boolean;
   }

   export default defineComponent({
      name: 'Signups',
      data(): RLData {
         return {
            trip: null,
            signups: [],
            draggingPax: null,
            draggingOverElement: null,
            draggingOverCount: 0,
            loading: false,
            errorMessage: null,
            infoMessage: null,
            fieldErrors: null,
            showingPaxDetailsId: null,
            paxDetailsTimer: null,
            unsavedChanges: false,
            dataLoaded: false,
            animateSaved: false,
            animateMoved: false
         }
      },
      components: {
         ModalLoading,
         ModalErrorMessage,
         ModalInfoMessage,
         CmsHeader,
         CmsBreadcrumbs,
         StateSelect,
      },
      directives: {
         focus: {
            mounted(el) {
               el.focus();
            },
         },
      },
      watch: {
         signups: {
            handler(oldVal, newVal) {
               if (this.dataLoaded) {
                  this.unsavedChanges = true;
               }
            },
            deep: true
         }
      },
      beforeRouteLeave(to, from, next) {
         const answer = this.unsavedChanges ? window.confirm('You have unsaved changes that will be lost. Are you sure you wish to leave?') : true;
         if (answer) {
            next()
         } else {
            next(false)
         }
      },
      created() {
         document.title = `Signups for Tour # ${this.$route.params.tourId} | Group Trips`;

         window.addEventListener("beforeunload", (e) => {
            if (this.unsavedChanges) {
               e.preventDefault()
               const message = "You have unsaved changes that will be lost. Are you sure you wish to leave?"
               e.returnValue = message
               return message
            }
         })

         const token = getAuthToken();

         if (token) {
            try {
               fetch(
                  `/api/gttrips/cms/${this.$route.params.tourId}`,
                  {
                     headers: {
                        "X-DT-TOKEN": token
                     }
                  }
               )
                  .then((r) => {
                     if (r.ok) {
                        r.json()
                           .then((json) => {
                              this.trip = json as Trip;
                              document.title = `Signups for Tour # ${this.trip.tourId} - ${this.trip.tourName} - ${this.tripDates} | Group Trips`;

                              fetch(
                                 `/api/gtsignups/${this.$route.params.tourId}`,
                                 {
                                    headers: {
                                       "X-DT-TOKEN": token!
                                    }
                                 }
                              )
                                 .then(r => {
                                    if (r.ok) {
                                       r.json()
                                          .then(json => {
                                             const pj = json as Signup[];
                                             pj.forEach(p => {
                                                if (!p.clientId) p.clientId = generateGuid();
                                             });
                                             this.signups = pj;
                                             this.$nextTick(() => { this.dataLoaded = true; });
                                          });
                                    } else {
                                       window.location.href = '/CMS/TripListPassengers';
                                    }
                                 })
                                 .catch(e => {
                                    console.error(e.toString());
                                    window.location.href = '/CMS/TripListPassengers';
                                 });
                              return;

                           })
                           .catch((e) => {
                              console.error(e.toString());
                              window.location.href = '/CMS/Login';
                           });
                     } else {
                        // Contact not found so go to the login page
                        window.location.href = '/CMS/Login';
                     }
                  })
                  .catch((e) => {
                     console.error(e.toString());
                     window.location.href = '/CMS/Login';
                  });
            } catch (e) {
               console.error(e.toString());
               window.location.href = '/CMS/Login';
            }
         }
      },
      computed: {
         rooms(): { [key: string]: any[] } {
            return groupBy(this.signups.filter(p => !p.isDeleted), "roomNum");
         },
         roomCount(): number {
            return Object.keys(groupBy(this.signups.filter(p => !p.isDeleted), "roomNum")).length;
         },
         roomMax(): number {
            return this.signups.filter(p => !p.isDeleted)
               .map((p) => Number(p.roomNum))
               .reduce((a: number, c: number) => (a > c ? a : c), 0);
         },
         paxCount(): number {
            return this.signups.filter(p => !p.isDeleted).length;
         },
         singleCount(): number {
            let count = 0;
            const rms = this.rooms;
            for (let roomNum in rms) {
               if (rms[roomNum].length == 1) count++;
            }
            return count;
         },
         doubleCount(): number {
            let count = 0;
            const rms = this.rooms;
            for (let roomNum in rms) {
               if (rms[roomNum].length == 2) count++;
            }
            return count;
         },
         tripleCount(): number {
            let count = 0;
            const rms = this.rooms;
            for (let roomNum in rms) {
               if (rms[roomNum].length == 3) count++;
            }
            return count;
         },
         tripDates(): string {
            if (this.trip) {
               return `${new Date(this.trip!.startDate.toString()).toLocaleDateString("en-US")} to ${new Date(this.trip!.returnDate.toString()).toLocaleDateString("en-US")}`;
            } else {
               return "";
            }
         }
      },
      methods: {
         addPassenger(roomNum: number) {
            const newPax: Signup = {
               clientId: generateGuid(),
               roomNum: roomNum,
               firstName: "",
               lastName: "",
               twoBeds: false,
               isDeleted: false,
               isViewed: true
            };
            this.signups.push(newPax);
            this.showingPaxDetailsId = newPax.clientId;
         },
         removePassenger(pax: Signup): void {
            let idx = this.signups.indexOf(pax);
            if (idx !== -1) {
               let name = (pax.firstName || pax.lastName) ? `${pax.firstName} ${pax.lastName}` : "New Passenger";
               if (confirm("Click OK to confirm removal of passenger " + name)) {
                  if (pax.passengerId) {
                     // If they have a ID then it has been saved, so mark as deleted rather than remove
                     pax.isDeleted = true;
                  } else {
                     this.signups.splice(idx, 1);
                  }
               }
            }
         },
         saveChanges() {
            this.animateSaved = false;
            this.fieldErrors = null;
            this.loading = true;

            const token = getAuthToken() ?? "";

            try {
               fetch(
                  `/api/gtsignups/${this.$route.params.tourId}`,
                  {
                     method: "put",
                     headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        "X-DT-TOKEN": token
                     },
                     body: JSON.stringify(this.signups),
                  }
               )
                  .then(r => {
                     if (r.ok) {
                        r.json()
                           .then(json => {
                              const pj = json as Signup[];

                              // Update local passenger objects with any server side controlled fields
                              pj.forEach(p => {
                                 if (p.clientId) {
                                    let pe = this.signups.find(x => x.clientId == p.clientId);
                                    if (pe) {
                                       pe.passengerId = p.passengerId;
                                    }
                                 }
                              });
                              this.loading = false;
                              this.animateSaved = true;
                              this.$nextTick(() => { this.unsavedChanges = false; });
                           });
                     } else if (r.status == 400) {
                        r.text().then((t) => {
                           const errs = parseApiError(t);
                           for (const err of errs.fieldErrors) {
                              if (err.index != null && err.index >= 0 && err.index < this.signups.length) {
                                 err.clientId = this.signups[err.index].clientId;
                              }
                           }
                           // Setting these will automatically update the display and show an error
                           this.loading = false;
                           this.fieldErrors = errs.fieldErrors;
                           this.errorMessage = errs.message;
                        });
                     } else if (r.status == 404) {
                        // Trip not found
                        this.unsavedChanges = false;
                        window.location.href = '/CMS/TripListPassengers';
                     } else {
                        this.loading = false;
                        this.errorMessage = "Something went wrong. Your changes may not have saved, sorry.";
                     }
                  })
                  .catch(e => {
                     this.loading = false;
                     console.error(e.toString());
                     this.errorMessage = "Something went wrong. Your changes may not have saved, sorry.";
                  });
            } catch (e) {
               this.loading = false;
               console.error(e.toString());
               this.errorMessage = "Something went wrong. Your changes may not have saved, sorry.";
            }

         },
         formatPhone(phoneRaw: string): string {
            if (phoneRaw) {
               let formatted = "";
               if (phoneRaw.length > 6)
                  formatted = `(${phoneRaw.substring(0, 3)}) ${phoneRaw.substring(
                     3,
                     6
                  )}-${phoneRaw.substring(6, 10)}`;
               else
                  formatted = `(${phoneRaw.substring(0, 3)}) ${phoneRaw.substring(
                     3,
                     6
                  )}`;

               return formatted;
            } else return "";
         },
         roomNumToChar(roomNum: number): string {
            let roomChar = "";
            if (roomNum > 26) {
               roomChar = String.fromCharCode("A".charCodeAt(0) + Math.floor((roomNum - 1) / 26) - 1);
            }

            roomChar += String.fromCharCode("A".charCodeAt(0) + ((roomNum - 1) % 26));

            return roomChar;
         },
         roomPaxCount(roomNum: number): number {
            return this.signups.filter((p) => !p.isDeleted && p.roomNum == roomNum).length;
         },
         roomType(paxCount: number): string {
            switch (paxCount) {
               case 1:
                  return "Single";
               case 2:
                  return "Double";
               case 3:
                  return "Triple";
               default:
                  return "N/A";
            }
         },
         startDrag(event: DragEvent, item: Signup) {
            if (event && event.dataTransfer) {
               event.dataTransfer.dropEffect = "move";
               event.dataTransfer.effectAllowed = "move";
               this.draggingPax = item;
            }
         },
         dragOver(event: DragEvent, roomNum: number): void {
            if (event && event.dataTransfer) {
               this.draggingOverCount++;

               if (this.draggingPax) {
                  if (
                     this.draggingPax.roomNum != roomNum &&
                     this.roomPaxCount(roomNum) < 3
                  ) {
                     if (event.target && event.target) {
                        let dragTarget = event.target as HTMLElement;
                        if (dragTarget.hasAttribute("dropzone")) {
                           dragTarget.style.backgroundColor = "lightgreen";
                           if (
                              this.draggingOverElement &&
                              this.draggingOverElement != dragTarget
                           ) {
                              this.draggingOverElement.style.backgroundColor = "";
                           }
                           this.draggingOverElement = dragTarget;
                        }
                     }
                     event.dataTransfer.dropEffect = "move";
                     event.dataTransfer.effectAllowed = "move";
                  }
                  event.preventDefault();
               }
            }
         },
         dragOut(event: DragEvent): void {
            if (event && event.target) {
               this.draggingOverCount--;
               if (this.draggingOverCount == 0) {
                  if (this.draggingOverElement)
                     this.draggingOverElement.style.backgroundColor = "";
               }
            }
         },
         dragDrop(roomNum: number): void {
            if (this.draggingPax) {
               if (
                  this.draggingPax.roomNum != roomNum &&
                  this.roomPaxCount(roomNum) < 3
               ) {
                  if (this.draggingOverElement)
                     this.draggingOverElement.style.backgroundColor = "";
                  this.draggingPax.roomNum = roomNum;
               }
            }
            this.draggingOverCount = 0;
         },
         dragEnd(): void {
            if (this.draggingOverElement) {
               this.draggingOverElement.style.backgroundColor = "";
               this.draggingOverElement = null;
            }
            this.draggingOverCount = 0;
         },
         showPaxDetails(el: HTMLElement, clientId: string) {
            if (this.paxDetailsTimer) {
               if (this.showingPaxDetailsId == clientId) {
                  clearTimeout(this.paxDetailsTimer);
                  this.paxDetailsTimer = null;
               }
            }

            el.style.zIndex = '10';
            this.showingPaxDetailsId = clientId;
         },
         hidePaxDetails(el: HTMLElement, clientId: string) {

            this.paxDetailsTimer = setTimeout(() => {
               if (this.showingPaxDetailsId == clientId) this.showingPaxDetailsId = null;

               this.paxDetailsTimer = null;

            }, 500);
         },
         cancelHidePaxDetails(el: HTMLElement, clientId: string) {
            if (this.paxDetailsTimer && this.showingPaxDetailsId == clientId) {
               el.style.zIndex = '10';
               clearTimeout(this.paxDetailsTimer);
               this.paxDetailsTimer = null;
            }
         },
         beforePaxDetailsFadeIn(el: HTMLElement) {
            if (el.previousElementSibling) (el.previousElementSibling as HTMLElement).style.zIndex = '10';
         },
         afterPaxDetailsFadeOut(el: HTMLElement) {
            if (el.previousElementSibling) (el.previousElementSibling as HTMLElement).style.zIndex = '';
         },
         allInRoomToBeMovedToRL(roomNum: number) {
            return this.signups.filter(p => p.roomNum == roomNum).every(p => p.moveToRL);
         },
         setRoomPaxToMoveToRL(roomNum: number, movetoRL: boolean) {
            const prevUnsavedChanges = this.unsavedChanges;
            this.signups.filter(p => p.roomNum == roomNum).forEach(p => p.moveToRL = (movetoRL as boolean));
            this.$nextTick(() => { this.unsavedChanges = prevUnsavedChanges; })
         },
         moveCheckedToRL() {

            if (this.unsavedChanges) {
               this.errorMessage = "Please save changes first.";
               return;
            }

            const paxToMove = this.signups.filter(p => p.moveToRL && this.allInRoomToBeMovedToRL(p.roomNum));

            if (paxToMove && paxToMove.length > 0) {

               const token = getAuthToken();

               if (token) {
                  try {
                     // First check if they exist on the rooming list
                     fetch(
                        `/api/gtsignups/${this.$route.params.tourId}/CheckExistsRL`,
                        {
                           method: "post",
                           headers: {
                              Accept: "application/json",
                              "Content-Type": "application/json",
                              "X-DT-TOKEN": token!
                           },
                           body: JSON.stringify(paxToMove),
                        }
                     )
                        .then(r => {
                           if (r.ok) {
                              r.json()
                                 .then(json => {
                                    const existingPax = json as Signup[];

                                    if (existingPax && existingPax.length > 0) {
                                       this.errorMessage = "The following passengers already appear to exist on the Rooming List, which must have unique names:\r\n" +
                                          existingPax.map(p => `${p.firstName} ${p.lastName}`).join("\r\n");
                                    } else {

                                       let sendWaivers = false;

                                       //if (paxToMove.some(p => p.email)) {
                                       //   sendWaivers = confirm("Do you also want to email online waiver requests to any of the passengers with email addresses?\r\n\r\n" +
                                       //      "Click OK for Yes or Cancel to just move to the Rooming List");
                                       //}

                                       // Then move them to the RL
                                       fetch(
                                          `/api/gtsignups/${this.$route.params.tourId}/MoveToRL?sendWaivers=${sendWaivers}`,
                                          {
                                             method: "post",
                                             headers: {
                                                Accept: "application/json",
                                                "Content-Type": "application/json",
                                                "X-DT-TOKEN": token!
                                             },
                                             body: JSON.stringify(paxToMove),
                                          }
                                       ).then(r => {
                                          if (r.ok) {
                                             // Remove moved passengers to match database
                                             paxToMove.forEach(p => p.isDeleted = true);
                                             this.animateMoved = true;
                                             this.$nextTick(() => this.unsavedChanges = false);
                                          } // if forbid then show error
                                          else if (r.status == 400) {
                                             this.errorMessage = "Only 26 rooms are allowed on a rooming list.";
                                          } else {
                                             this.errorMessage = "Something went wrong, sorry. Please refresh and try again later.";
                                          }
                                       })
                                    }
                                 });
                           } else {
                              // Trip does not exist
                              window.location.href = '/CMS/TripListPassengers';
                           }
                        })
                        .catch(e => {
                           console.error(e.toString());
                           this.errorMessage = "Something went wrong, sorry. Please try again later.";
                        });
                  } catch (e) {
                     console.error(e);
                     this.errorMessage = "Something went wrong, sorry. Please try again later.";
                  }
               }

            } else {
               this.errorMessage = "No rooms have been checked for moving passengers to the Rooming List. Please click the checkbox next to the room titles for those your want to move.";
            }
         }
      }
   });

